import React, { useCallback, useEffect, useRef, useState } from 'react';
import Content from '../../components/Content/Content';
import styles from './HomePage.module.scss';
import ProtectedPageSidebar from '../../components/Sidebar/ProtectedPageSidebar';
import { useUserContext } from '../../context/UserContext';
import DashboardCards from '../../features/Dashboard/components/DashboardCards/DashboardCards';
import { LoadingSpinner } from '../../components/Icon/icons';
import VideoService from '../../service/VideoService';
import RightSidebarMenu from '../../components/RightVideoActionMenu/RightSidebarMenu';
import { useLibraryContext } from '../../context/LibraryContext';
import { elapsedTimeUntilNow } from '../../utils/TimeUtil';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import MobileHeader from '../../components/MobileHeader';
import Icon from '../../components/Icon/Icon';
import InstallExtensionModal from '../../components/InstallExtensionModal/InstallExtensionModal';
import { useHistory } from 'react-router-dom';
import { isChrome } from '../../../common/utils/browser-detector';
import Onboarding from '../../components/Onboarding';
import Assignment from '../../components/Assignment/Assignment';
import AssignmentList from '../../components/AssignmentList/AssignmentList';
import KnowledgeBaseList from '../../components/KnowledgeBaseList/KnowledgeBaseList';
import UploadVideoModal from '../../components/UploadVideoModal/UploadVideoModal';
import MobileUploadButton from '../../components/MobileUploadButton/MobileUploadButton';
import { RecordingOnWebModal } from '../../components/RecordingOnWebModal/RecordingOnWebModal';
import OnboardingVideoCard from '../../features/Home/components/OnboardingVideoCard/OnboardingVideoCard';
import HomeButton from '../../features/Home/components/HomeButton/HomeButton';
import { useLanguageContext } from '../../context/LanguageContext';
import StoryboardWidget from '../../features/Home/components/StoryboardWidget/StoryboardWidget';
import HomePageSectionHeader from '../../features/Home/components/HomePageSectionHeader/HomePageSectionHeader';
import useOutsideClick from '../../hooks/useOutsideClick';
import CloseButton from '../../components/CloseButton/CloseButton';
import { APP_USER_ROLE } from '../../constants/UserRoles';
import { useLocation } from 'react-router-dom';
import PageHeader from 'engage/components/PageHeader/PageHeader';

const onboardingExplanationVideos = [
  {
    id: 'web',
    title: 'home_page.onboarding.web_explanation.title',
    description: 'home_page.onboarding.web_explanation.description',
    thumbnail: 'https://prod-static-video-resources.s3.eu-west-1.amazonaws.com/manual-uploads/learn_web_recordings_thumb.png',
    link: 'https://secure.vormats.com/engage/897aa143-4751-458d-b501-77efc69ee02c',
  },
  {
    id: 'mobile',
    title: 'home_page.onboarding.mobile_explanation.title',
    description: 'home_page.onboarding.mobile_explanation.description',
    thumbnail: 'https://prod-static-video-resources.s3.eu-west-1.amazonaws.com/manual-uploads/learn_mobile_recordings_thumb.png',
    link: 'https://secure.vormats.com/engage/fc93d6c9-1e7d-481d-9e2f-7bdb74134023',
  },
];

const HomePage = () => {
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const [isWebActivated, setIsWebActivated] = useState(true);
  const [isOnboardingShowing, setIsOnboardingShowing] = useState(false);
  const [isShowExtensionModal, setIsShowExtensionModal] = useState(false);
  const [isShowRecordingRequests, setIsShowRecordingRequests] = useState(false);
  const [isShowKnowledgeBase, setIsShowKnowledgeBase] = useState(false);

  const [assignments, setAssignments] = useState([]);
  const [isAssignmentsLoading, setIsAssignmentsLoading] = useState(true);

  const [isShowUploadVideoModal, setIsShowUploadVideoModal] = useState(false);
  const [showScriptOptions, setShowScriptOptions] = useState(false);

  const {
    user,
    userLoading,
    appConfig,
    isStoryboardGeneratorEnabled,
    isProductTrainingEnabled,
    isProfessionalRequestEnabled,
    isTypebotEnabled,
    userRole,
  } = useUserContext();

  const { language } = useLanguageContext();

  const [recentVideos, setRecentVideos] = useState([]);
  const [recentVideosAmount, setRecentVideosAmount] = useState(0);
  const [recentVideosLoading, setRecentVideosLoading] = useState(true);

  const [isShowAssignmentMobileModal, setIsShowAssignmentMobileModal] = useState(false);

  const [knowledgeVideos, setKnowledgeVideos] = useState([]);
  const [filteredKnowledgeVideos, setFilteredKnowledgeVideos] = useState([]);
  const [recentKnowledgeVideos, setRecentKnowledgeVideos] = useState([]);

  const { selectedCard } = useLibraryContext();

  const history = useHistory();

  const { search } = useLocation();

  const { t, i18n } = useTranslation();

  const scriptOptionsRef = useRef(null);
  useOutsideClick(scriptOptionsRef, () => showScriptOptions && setShowScriptOptions(false));

  const getRecentVideos = useCallback(async () => {
    const query = {
      folder: null,
      omitFiledStories: true,
      include_empty: true,
      size: 4,
      searchTerm: '',
      page: 0,
      sort: ['createdOn,desc'],
      archived: false,
    };

    try {
      setRecentVideosLoading(true);
      const { result, pagination } = await VideoService.getVideos(query);

      if (result.length > 0) {
        setIsWebActivated(true);
      } else {
        setIsWebActivated(false);
      }
      setRecentVideos(result);
      setRecentVideosAmount(pagination?.total || 0);
    } catch (err) {
      setRecentVideos([]);
      setRecentVideosAmount(0);
    } finally {
      setRecentVideosLoading(false);
    }
  }, []);

  const handleAssignmentClick = useCallback(async (assignment, videoPage = false) => {
    if (isMobileView) {
      setIsShowAssignmentMobileModal(true);
      return;
    }

    if (!assignment.completedAt) {
      try {
        await VideoService.readAssignments(assignment.storyId);
      } catch (e) {
        console.log('Read assignment error: ', e);
      }
    }

    if (userRole === APP_USER_ROLE.VIDEO_EDITOR) {
      history.push(`/eg/edit-external/${assignment.storyId}`);
      return;
    }

    history.push(videoPage ? `/eg/video/${assignment.storyId}` : `/eg/storyboard/${assignment.storyId}`);
  }, [userRole]);

  const handleStartStoryboardProject = useCallback(async () => {
    history.push('/eg/storyboard/new', { create: false });
  }, [isStoryboardGeneratorEnabled]);

  const handleStartTraining = () => {
    window?.Appcues?.track('start_product_training_clicked');
  };

  const handleCreateScript = useCallback(async () => {
    history.push('/eg/storyboard/new', { create: true });
  }, []);

  const handleRecord = () => {
    const extensionId = process.env.EXT_ID;

    const extensionExists = document.getElementById(extensionId);
    if (extensionExists) {
      window.postMessage({ type: extensionId, message: 'OPEN_POPUP_IN_IFRAME' }, '*');
    } else {
      window.location = `vormats-video-storyteller://`;
      setTimeout(() => {
        if (localStorage.getItem('recorderInstalled') !== 'true') {
          setIsShowExtensionModal(true);
        }
      }, 1000);
    }
  };

  const handleProfessionalEditor = () => {
    window?.Appcues?.track('professional_video_request', { language: language.lng });
  };

  const handleSeeAllVideos = () => {
    history.push(`/eg/dashboard`);
  };

  useEffect(() => {
    if (user !== null) {
      setIsOnboardingShowing(!Boolean(user?.onboarded));

      const extensionId = process.env.EXT_ID;
      window.postMessage({ type: extensionId, message: 'LOGIN_FROM_APP' }, '*');

      const getAssignments = async () => {
        try {
          const result = await VideoService.getAllAssignments();
          const tempAssignments = result.map(({ id, name, status, assignments }) => {
            const inviterAvatar = assignments[0].inviter?.avatar;
            const inviterName = assignments[0].inviter?.name;
            const completedAt = assignments.some(item => item.completedAt === null) ? null : assignments[0].completedAt;

            const [updatedDate] = assignments
              .map(({ updatedAt }) => ({ stringDate: updatedAt, currentDate: new Date(updatedAt) }))
              .sort((a, b) => b.currentDate - a.currentDate);

            const updatedTime = elapsedTimeUntilNow(updatedDate.stringDate);

            const [expirationDate] = assignments
              .map(({ expiresAt }) => ({ stringDate: expiresAt, currentDate: new Date(expiresAt) }))
              .sort((a, b) => a.currentDate - b.currentDate);

            const readAt = assignments.some(item => item.readAt === null) ? null : assignments[0].readAt;

            return {
              storyId: id,
              storyStatus: status,
              assignmentName: name,
              inviterAvatar,
              inviterName,
              updatedTime,
              expirationDate: expirationDate.stringDate,
              readAt,
              completedAt,
              newestAssignmentDate: updatedDate.currentDate,
            };
          });

          // Sort the Array by newest
          const filtered = tempAssignments.sort((a, b) => new Date(b.newestAssignmentDate) - new Date(a.newestAssignmentDate));

          // Split the Array into Read and Unread Arrays
          const unreadArray = filtered.filter(item => item.readAt === null);
          const readArray = filtered.filter(item => item.readAt !== null);

          // Show first the new assignments and then the read ones
          const finalArray = [...unreadArray, ...readArray];

          setAssignments(finalArray);
        } catch (err) {
          console.log(err);
        } finally {
          setIsAssignmentsLoading(false);
        }
      };

      getAssignments();
      getRecentVideos();
    }
  }, [user]);

  const setAndFilterKnowledgeVideos = (knowledgeVideos, lang) => {
    if (lang === 'nl') {
      var videoDataFiltered = knowledgeVideos.filter(function (el) {
        return el.language === 'nl';
      });
    } else {
      var videoDataFiltered = knowledgeVideos.filter(function (el) {
        return el.language === 'en';
      });
    }
    setFilteredKnowledgeVideos(videoDataFiltered);
    setRecentKnowledgeVideos(videoDataFiltered.slice(0, 4));
  };

  const toggleShowScriptOptions = () => {
    setShowScriptOptions(prevState => !prevState);
  };

  useEffect(() => {
    setAndFilterKnowledgeVideos(knowledgeVideos, i18n.language);
  }, [knowledgeVideos]);

  const fetchKnowledgeVideos = async () => {
    const resp = await fetch('https://prod-static-video-resources.s3.eu-west-1.amazonaws.com/manual-uploads/knowledge_videos.json');
    const data = await resp.json();
    setKnowledgeVideos(data);
  };

  useEffect(() => {
    if (isShowKnowledgeBase) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isShowKnowledgeBase]);

  useEffect(() => {
    fetchKnowledgeVideos();

    if (search.includes('openExtension')) {
      const extensionId = process.env.EXT_ID;
      window.postMessage({ type: extensionId, message: 'OPEN_POPUP_IN_IFRAME' }, '*');
    }

    if (search.includes('chat')) {
      $crisp.push(['do', 'chat:show']);
      $crisp.push(['do', 'chat:open']);
    }
  }, []);

  useEffect(() => {
    setAndFilterKnowledgeVideos(knowledgeVideos, i18n.language);
  }, [i18n.language]);

  if (user === null || userLoading) {
    return null;
  }

  if (isChrome && isOnboardingShowing && !userLoading) {
    return <Onboarding setIsOnboardingShowing={setIsOnboardingShowing} />;
  }

  return (
    <>
      <ProtectedPageSidebar />
      <MobileHeader libraryMode={false} userAvatar={user?.avatarUri} userFirstLetter={user?.userFullName.charAt(0).toUpperCase()} />
      <Content className={styles.content}>
        {isShowKnowledgeBase && (
          <KnowledgeBaseList
            knowledgeVideos={filteredKnowledgeVideos}
            setIsShowKnowledgeBase={setIsShowKnowledgeBase}
            isMobileView={isMobileView}
          />
        )}

        {isShowRecordingRequests && (
          <AssignmentList
            assignments={assignments}
            isAssignmentsLoading={isAssignmentsLoading}
            handleAssignmentClick={handleAssignmentClick}
            setIsShowRecordingRequests={setIsShowRecordingRequests}
          />
        )}
        {!isShowKnowledgeBase && !isShowRecordingRequests && (
          <div className={styles.mainWrapper}>
            <div className={styles.mainText}>
              <PageHeader title={isWebActivated ? t('home_page.home') : t('home_page.welcome')} />
              {!isMobileView && isProductTrainingEnabled && (
                <HomeButton
                  label="home_page.start_product_training"
                  onClick={handleStartTraining}
                  color="rgba(255, 163, 26, 1)"
                  icon="lamp"
                  keepIconColor={true}
                />
              )}
            </div>

            {appConfig && (!!assignments.length || isAssignmentsLoading) && (
              <div className={cx(styles.sectionWrapper, styles.assignments)}>
                <div className={cx(styles.recordingRequestsHeader, styles.column)}>
                  <HomePageSectionHeader
                    // className={styles.sectionHeader}
                    icon={'assignments'}
                    title={t('home_page.assignments.header')}
                    description={t('home_page.assignments.sub_header')}
                  />
                  {assignments.length > 3 && (
                    <div className={styles.seeAllContainer} onClick={() => setIsShowRecordingRequests(true)}>
                      <span className={styles.seeAllText}>{t('home_page.see_all')}</span>
                      <Icon src="rightArrow" className={styles.seeAllIcon} />
                    </div>
                  )}
                </div>
                {isAssignmentsLoading ? (
                  <div className={styles.spinnerContainer}>
                    <LoadingSpinner theme="light" />
                  </div>
                ) : (
                  <div className={styles.assignmentsContainer}>
                    {assignments.slice(0, 3).map(assignment => (
                      <Assignment key={assignment.storyId} assignment={assignment} handleAssignmentClick={handleAssignmentClick} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {!isWebActivated && (
              <div className={cx(styles.sectionWrapper, styles.onboardingVideoCards)}>
                <h2 className={styles.explanationHeader}>{t('home_page.onboarding.title')}</h2>
                <div className={styles.explanationWrapper}>
                  {onboardingExplanationVideos.map(({ id, ...item }) => (
                    <OnboardingVideoCard key={item.id} {...item} />
                  ))}
                </div>
              </div>
            )}

            {!isMobileView && (
              <div className={styles.sectionWrapper}>
                {appConfig && !isTypebotEnabled && <span className={styles.titleSecond}>{t('home_page.create_video')}</span>}
                {appConfig ? (
                  <div>
                    {isTypebotEnabled && <StoryboardWidget className={styles.widget} />}
                    {isTypebotEnabled && <HomePageSectionHeader
                      className={styles.sectionHeader}
                      title={t('home_page.start_recording.header')}
                      description={t('home_page.start_recording.sub_header')}
                    />}
                    <div className={styles.recordOptionsContainer}>
                      <HomeButton
                        label={"home_page.start_with_a_script"}
                        icon="pencil_black"
                        onClick={isStoryboardGeneratorEnabled ? toggleShowScriptOptions : handleCreateScript}
                        color="rgba(209, 170, 161, 1)"
                        keepIconColor={true}
                      />
                      <HomeButton
                        label="home_page.record_or_upload"
                        icon="record"
                        onClick={handleRecord}
                        color="rgba(55, 193, 160, 1)"
                        keepIconColor={true}
                        disabled={showScriptOptions}
                      />
                      {isProfessionalRequestEnabled && (
                        <HomeButton
                          label="home_page.professionally_edited"
                          icon="community"
                          onClick={handleProfessionalEditor}
                          color="rgba(98, 0, 238, 1)"
                          isNew={true}
                          disabled={showScriptOptions}
                        />
                      )}
                    </div>
                    <div ref={scriptOptionsRef} className={cx(styles.scriptOptions, { [styles.active]: showScriptOptions })}>
                      <div style={{ display: 'flex' }}>
                        <HomeButton
                          label="home_page.script_generator"
                          icon="scriptGenerator"
                          onClick={handleStartStoryboardProject}
                          color="rgba(209, 170, 161, 1)"
                          keepIconColor={true}
                          className={styles.scriptGeneratorBtn}
                        />
                        <HomeButton
                          label="home_page.write_yourself"
                          icon="pencil_black"
                          onClick={handleCreateScript}
                          color="rgba(209, 170, 161, 1)"
                          keepIconColor={true}
                        />
                        <CloseButton variant="secondary" onClose={toggleShowScriptOptions} className={styles.closeBtn} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.spinnerContainer}>
                    <LoadingSpinner theme="light" />
                  </div>
                )}
              </div>
            )}

            {isMobileView && <MobileUploadButton setIsShowUploadVideoModal={setIsShowUploadVideoModal} />}

            {isWebActivated && (
              <>
                {(!!recentVideos.length || recentVideosLoading) && (
                  <div className={styles.sectionWrapper}>
                    <div className={styles.recentVideosHeader}>
                      <span className={styles.titleSecond}>{t('home_page.recent_videos')}</span>
                      {recentVideosAmount > 4 && (
                        <div className={styles.seeAllContainer} onClick={handleSeeAllVideos}>
                          <span className={styles.seeAllText}>{t('home_page.see_all')}</span>
                          <Icon src="rightArrow" className={styles.seeAllIcon} />
                        </div>
                      )}
                    </div>
                    {recentVideosLoading ? (
                      <div className={styles.spinnerContainer}>
                        <LoadingSpinner theme="light" />
                      </div>
                    ) : (
                      <DashboardCards
                        searchInput=""
                        videos={recentVideos}
                        listMode={false}
                        loadVideos={getRecentVideos}
                        cardsHeaderId="cardsHeaderID"
                        colClassnames={filteredKnowledgeVideos.length > 0 ? styles.fixMargin : ''}
                        videoTabHeader={false}
                        alt="Click for Details"
                      />
                    )}
                  </div>
                )}
              </>
            )}

            {filteredKnowledgeVideos && filteredKnowledgeVideos.length > 0 && (
              <div className={styles.sectionWrapper}>
                <div className={styles.recordingRequestsHeader}>
                  <span className={styles.titleFirst}>{t('home_page.knowledge_base')}</span>
                  <div className={styles.seeAllContainer} onClick={() => setIsShowKnowledgeBase(true)}>
                    <span className={styles.seeAllText}>{t('home_page.see_all')}</span>
                    <Icon src="rightArrow" className={styles.seeAllIcon} />
                  </div>
                </div>

                <DashboardCards
                  hideActions={true}
                  searchInput=""
                  videos={recentKnowledgeVideos}
                  listMode={false}
                  cardsHeaderId="cardsHeaderID"
                  selectable={false}
                  videoTabHeader={false}
                />
              </div>
            )}
          </div>
        )}
        {!!Object.keys(selectedCard).length && <RightSidebarMenu userId={user.username} />}
      </Content >
      <InstallExtensionModal
        open={isShowExtensionModal}
        onClose={() => setIsShowExtensionModal(false)}
        handleShowUploadVideoModal={() => setIsShowUploadVideoModal(true)}
      />
      <UploadVideoModal
        isShow={isShowUploadVideoModal}
        onClose={() => setIsShowUploadVideoModal(false)}
        loadVideos={getRecentVideos}
      />
      <RecordingOnWebModal show={isShowAssignmentMobileModal} onClose={() => setIsShowAssignmentMobileModal(false)} />
    </>
  );
};

export default HomePage;
